'use client';

import type { AnswerValue } from '@repo/contracts/cardfinder/funnel/types';
import { useContext } from 'react';
import { AnswerStoreContext } from './AnswerStoreProvider';
import type { UseAnswerResult } from './types';

function useAnswerStoreContext() {
  const ctx = useContext(AnswerStoreContext);
  if (!ctx) throw new Error('useAnswer must be used within AnswerStoreProvider');
  return ctx;
}

export function useAnswerStore() {
  return useAnswerStoreContext().store;
}

export function useAnswerDispatch() {
  return useAnswerStoreContext().dispatch;
}

export function useAnswer<T extends AnswerValue>(stepId: string): UseAnswerResult<T> {
  const { store, dispatch } = useAnswerStoreContext();
  return {
    value: store.answers[stepId] as T | undefined,
    setAnswer: (next: T) => dispatch({ type: 'SET_ANSWER', stepId, value: next }),
    clear: () => dispatch({ type: 'CLEAR_ANSWER', stepId }),
  };
}

export function useIsAnswerHydrated() {
  return useAnswerStoreContext().store.isHydrated;
}

export function useSubmittedAnswers() {
  return useAnswerStoreContext().store.submittedAnswers;
}

export function useShouldSkipRecommendations() {
  return useAnswerStoreContext().store.shouldSkipRecommendations;
}

export function useResetAnswers() {
  const { dispatch } = useAnswerStoreContext();
  return () => dispatch({ type: 'RESET_ANSWERS' });
}
